// @flow
import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import {Divider, Grid, Typography} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import makeStyles from '@material-ui/core/styles/makeStyles';

import CustomModal from '../../../../elements/CustomModal/CustomModal';

import {colors} from '../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';

import {useAdditionalInformation} from '../../../configure/sales-process/hooks';

import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {actions as ManualOrderActions} from '../../../manual-order/duck';
import TextInput from '../../../configure/sales-process/components/Inputs/TextInput';
import NumberInput from '../../../configure/sales-process/components/Inputs/NumberInput';

import {ChecklistInput} from '../../../configure/sales-process/components/Inputs/ChecklistInput';
import CalendarInput from '../../../configure/sales-process/components/Inputs/CalendarInput';
import {useAdditionalInformationContext} from '../../../configure/sales-process/context/hooks';

import {type Order, type CheckoutInput, INPUT_TYPE} from '../../../../entities';
import type {CheckoutRequest} from '../../../manual-order/api';
import type {RootState} from '../../../index';
import type {Dispatch} from 'redux';

export default connect(
  (state: RootState) => ({
    editOrderSuccess: state.manualOrder.editOrderSuccess,
    editOrderError: state.manualOrder.editOrderError,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...ManualOrderActions,
      },
      dispatch
    )
)(EditAdditionalInfoModal);

type Props = {
  isSaving?: boolean,
  open: boolean,
  handleClose: () => void,
  order: Order,
  editOrder: ({checkoutRequest: CheckoutRequest}) => void,
};
const useStyles = makeStyles(theme => ({
  root: {
    // padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 340,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  textField: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
  },
  button: {
    height: 40,
    fontSize: '1rem',
    alignSelf: 'stretch',
    marginBottom: theme.spacing(1),
  },
  gridInputs: {
    marginBottom: '16px',
  },
  error: {
    margin: 0,
    color: '#f44336',
    fontSize: '0.75rem',
  },
}));

function EditAdditionalInfoModal(props: Props) {
  const {isSaving, open, handleClose, order, editOrder} = props;
  const classes = useStyles();

  const {
    additionalInformation: addInfoContextValues,
    setAdditionalInformationValidate,
  } = useAdditionalInformationContext();

  const {additionalInformation} = useAdditionalInformation();

  const {register, handleSubmit, errors} = useForm();

  const handleCloseModal = useCallback(() => {
    if (!isSaving) {
      handleClose();
    }
  }, [handleClose, isSaving]);

  const isValidCustomAdditionalInformation = () => {
    let isValid = true;

    for (let id in addInfoContextValues) {
      if (!isValid) return;
      const obj = addInfoContextValues[id];
      if (!!obj.error) isValid = false;
    }
    return isValid;
  };

  const buildAdditionalInformationRaw = () => {
    const addInfoArray = [];
    for (let addInfoId in addInfoContextValues) {
      const elem = addInfoContextValues[addInfoId];
      const value = elem.value ?? [];
      const hasValues = value.filter(el => !!el).length > 0;

      const additionalInformationField = additionalInformation?.find(
        el => el.id === addInfoId
      );

      if (hasValues)
        addInfoArray.push({
          storeOrderAdditionalInformationId: addInfoId,
          additionalInformation: {
            value: addInfoContextValues[addInfoId].value,
            number: additionalInformationField
              ? additionalInformationField.number
              : 0,
          },
        });
    }
    return addInfoArray;
  };

  const buildTextInfo = (data: {[key: string]: any}) => {
    return Object.entries(data)
      .filter(([_, value]: [any, any]) => value?.length > 0)
      .map(([key, value]) => {
        const additionalInformationField = additionalInformation?.find(
          el => el.id === key
        );
        return {
          storeOrderAdditionalInformationId: key,
          additionalInformation: {
            value: [value],
            number: additionalInformationField
              ? additionalInformationField.number
              : 0,
          },
        };
      });
  };

  const handleAmountReceivedSubmit = data => {
    setAdditionalInformationValidate(true);

    const isValid = isValidCustomAdditionalInformation();
    if (!isValid) return;

    const registeredValues = buildTextInfo(data);
    const request = {
      orderData: {
        additionalInformation: [
          ...buildAdditionalInformationRaw(),
          ...registeredValues,
        ],
      },
    };
    editOrder({checkoutRequest: {id: order.id, request}});
  };

  const buildCheckoutInput = (elm: CheckoutInput) => {
    const type = elm.inputType;
    switch (type) {
      case INPUT_TYPE.NUMBER:
        return (
          <NumberInput
            register={register}
            errors={errors}
            item={elm}
            order={order}
          />
        );
      case INPUT_TYPE.TEXT:
        return (
          <TextInput
            register={register}
            errors={errors}
            item={elm}
            order={order}
          />
        );
      case INPUT_TYPE.CHECKLIST:
        return (
          <ChecklistInput
            errors={errors}
            classes={classes}
            item={elm}
            order={order}
          />
        );
      case INPUT_TYPE.DATE_PICKER:
        return <CalendarInput item={elm} classes={classes} order={order} />;
      default:
        return null;
    }
  };

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Typography className={classes.title} align="center">
          Editar información adicional
        </Typography>
        <Divider className={classes.divider} />
        <Grid container spacing={2} className={classes.gridInputs}>
          {additionalInformation?.map(elem => (
            <Grid item xs={12}>
              {buildCheckoutInput(elem)}
            </Grid>
          ))}
        </Grid>
        {/* <CustomTextField
          label="Fecha de entrega"
          name="deliveryDate"
          className={classes.textField}
          // defaultValue={
          //   (order.additionalInformation &&
          //     order.additionalInformation.deliveryDate) ||
          //   ''
          // }
          error={!!errors.deliveryDate}
          helperText={errors.deliveryDate ? errors.deliveryDate.message : ''}
          inputRef={register({
            // required: 'Campo obligatorio',
          })}
          type="text"
          disabled={isSaving}
        />
        <CustomTextField
          label="Comentario"
          name="additionalInformation"
          className={classes.textField}
          // defaultValue={
          //   (order.additionalInformation &&
          //     order.additionalInformation.additionalInformation) ||
          //   ''
          // }
          error={!!errors.additionalInformation}
          helperText={
            errors.additionalInformation
              ? errors.additionalInformation.message
              : ''
          }
          inputRef={register({
            // required: 'Campo obligatorio',
          })}
          type="text"
          disabled={isSaving}
        /> */}
        <CustomButton
          className={classes.button}
          variant="contained"
          onClick={handleSubmit(handleAmountReceivedSubmit)}
          disabled={isSaving}
        >
          Guardar cambios
        </CustomButton>
        <CustomButton
          className={classes.button}
          onClick={handleCloseModal}
          disabled={isSaving}
        >
          Cancelar
        </CustomButton>
      </div>
    </CustomModal>
  );
}

EditAdditionalInfoModal.propTypes = {
  isSaving: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  editOrder: PropTypes.func,
};
